import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { AngularFireStorage } from "@angular/fire/storage";
import { finalize, map, takeUntil } from "rxjs/operators";

import { FileStorageService } from "../../services/file-storage.service";
import { UUID } from "angular2-uuid";
import { UserService } from "../../@core/services/user.service";
import { Subject } from "rxjs";
import { User } from "../../@core/data/users";
import { FileStorage } from "../../models/file-storage.model";
import * as _ from "lodash";

@Component({
  selector: "ngx-upload-image",
  styleUrls: ["./upload-image.component.scss"],
  templateUrl: "./upload-image.component.html",
})
export class UploadImageComponent implements OnInit, OnDestroy {
  private destroy$: Subject<void> = new Subject<void>();

  @Input() imageURL: string;
  @Input() size?: string;
  @Input() type?: string; //single / multiple
  @Input() mode?: string; //firebase / amazons3 / imagekit
  @Input() path?: string; //users/avatars / /users/uploads :( not compress images) / /users/images : compress 400x400
  @Output() onUploadSuccess: EventEmitter<any> = new EventEmitter();
  @Output() onSelectSuccess: EventEmitter<any> = new EventEmitter();

  @ViewChild("fileUpload", { static: false }) fileUpload: ElementRef;
  files = [];
  loading = false;
  urlBg = "/assets/images/image-upload-bg.jpg";

  percentage = 0;
  uid = "";
  message = "";

  pageSize = 10;
  cursor: any;
  done = false;
  init = false;
  firstData = [];
  moreData = [];
  imageObject = [];

  constructor(
    private storage: AngularFireStorage,
    private userService: UserService,
    private uploadService: FileStorageService
  ) {}

  ngOnInit(): void {
    if (!this.imageURL) this.imageURL = this.urlBg;
    if (!this.size) this.size = "medium";
    if (!this.type) this.type = "single";
    if (!this.mode) this.mode = "firebase"; //amazons3
    if (!this.path) this.path = "/users/images"; //or: /users/uploads :( not compress images) / /users/images : compress 400x400

    this.userService
      .getCurrentUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe((user: User) => {
        if (user && user.uid != this.uid) {
          this.uid = user.uid;

          if (this.type == "multiple") {
            this.getFirst();
          }
        } else {
          this.uid = "anonymous";
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getFirst(): void {
    this.loading = true;

    this.uploadService
      .getFiles(this.uid, "image")
      .snapshotChanges(["added", "removed"])
      .pipe(
        map((changes) =>
          changes.map((c) => ({
            id: c.payload.doc.id,
            ...c.payload.doc.data(),
            doc: c.payload.doc,
          }))
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((data) => {
        if (!this.cursor && data.length > 0) {
          this.cursor = data[data.length - 1].doc;
        }

        data.forEach((com) => {
          let item = _.find(this.imageObject, ["image", com.url]);
          if (!item) {
            this.imageObject.push({
              image: com.url,
              thumbImage: com.thumbnailUrl ? com.thumbnailUrl : com.url,
              title: "",
            });
          }
        });

        this.loading = false;
        this.init = true;
      });
  }

  loadMore(): void {
    this.loading = true;

    this.uploadService
      .getMoreFiles(this.uid, "image", this.pageSize, this.cursor)
      .snapshotChanges(["added", "removed"])
      .pipe(
        map((changes) =>
          changes.map((c) => ({
            id: c.payload.doc.id,
            ...c.payload.doc.data(),
            doc: c.payload.doc,
          }))
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((data) => {
        if (data.length > 0) {
          this.cursor = data[data.length - 1].doc;
        } else {
          this.done = true;
        }

        data.forEach((com) => {
          let item = _.find(this.imageObject, ["image", com.url]);
          if (!item) {
            this.imageObject.push({
              image: com.url,
              thumbImage: com.thumbnailUrl ? com.thumbnailUrl : com.url,
              title: "",
            });
          }
        });

        this.loading = false;
      });
  }

  selectedImage(value) {
    if (value) {
      this.onSelectSuccess.emit(value);
    }
  }

  onClickBrowseFile() {
    this.loading = false;
    this.message = "";
    this.files = [];
    const fileUpload = this.fileUpload.nativeElement;
    fileUpload.onchange = () => {
      for (let index = 0; index < fileUpload.files.length; index++) {
        const file = fileUpload.files[index];
        this.files.push({ data: file, inProgress: false, progress: 0 });
      }

      this.uploadFiles();
    };
    fileUpload.click();
  }

  private uploadFiles() {
    this.fileUpload.nativeElement.value = "";
    this.files.forEach((file) => {
      console.log("uploadFiles/file:", file);

      if (file.data.size > 2000000) {
        this.message = "Dung lượng hình ảnh vượt quá 2MB";
      } else if (!file.data.type.toLowerCase().includes("image")) {
        this.message = "Xin vui lòng chọn hình ảnh";
      } else {
        this.message = "";
        switch (this.mode) {
          case "imagekit":
            this.uploadFileImageKit(file);
            break;
          case "amazons3":
            this.uploadFileAmazonS3(file);
            break;
          case "firebase":
            this.uploadFileFirebase(file);
            break;
          default:
            this.uploadFileFirebase(file);
            break;
        }
      }
    });
  }

  //AmazonS3
  private uploadFileAmazonS3(file) {
    console.log("uploadFileAmazonS3", file);

    const formData = new FormData();
    formData.append("file", file.data);
    formData.append("fileName", file.data.name);
    file.inProgress = true;
    this.loading = true;

    this.uploadService.uploadImageS3(formData).subscribe(
      (obj) => {
        this.loading = false;
        this.imageURL = obj;
        this.onUploadSuccess.emit(obj);
      },
      (error) => {
        this.loading = false;
      }
    );
  }

  //ImageKit.io
  private async uploadFileImageKit(file) {
    console.log("uploadFileImageKit", file);

    const dateNow = new Date().getTime();
    const fileId = UUID.UUID();
    const fileName = `${this.uid}_${dateNow}_${fileId}`;

    var formData = new FormData();
    formData.append("file", file.data);
    formData.append("fileName", fileName); //file.data.name
    formData.append("folder", this.path ? this.path : "user/uploads");

    this.loading = true;

    (await this.uploadService.uploadImageKit(formData)).subscribe(
      (obj) => {
        // console.log("uploadFileImageKit/url:", obj);

        this.loading = false;
        this.imageURL = obj;
        this.onUploadSuccess.emit(obj);
      },
      (error) => {
        this.loading = false;
      }
    );
  }

  //Firebase Storage
  private uploadFileFirebase(file) {
    console.log("uploadFileFirebase", file);

    if (file) {
      this.loading = true;

      let fileKey = "";
      if (this.path == "/users/avatars") {
        fileKey = `${this.uid}`;
      } else {
        const dateNow = new Date().getTime();
        const fileId = UUID.UUID();
        fileKey = `${this.uid}_${dateNow}_${fileId}`;
      }

      const filePath = `${this.path}/${fileKey}`;
      const storageRef = this.storage.ref(filePath);
      const uploadTask = this.storage.upload(filePath, file.data);

      uploadTask
        .snapshotChanges()
        .pipe(
          finalize(() => {
            storageRef.getDownloadURL().subscribe((downloadURL) => {
              console.log("uploadFileFirebase/downloadURL:", downloadURL);

              this.imageURL = downloadURL;

              const resizeUrl = `${fileKey}_400x400`;
              downloadURL = downloadURL.replace(fileKey, resizeUrl);
              console.log(
                "uploadFileFirebase/downloadURL/resizeUrl:",
                downloadURL
              );

              this.onUploadSuccess.emit(downloadURL);

              //save user upload to database
              const fileUpload: FileStorage = new FileStorage(file);
              fileUpload.key = fileKey;
              fileUpload.url = downloadURL;
              fileUpload.name = file.data.name;
              fileUpload.size = file.data.size;
              fileUpload.type = "image";
              fileUpload.path = filePath;
              fileUpload.source = "firebase";
              this.uploadService.saveFileData(fileUpload);

              this.loading = false;
            });
          })
        )
        .subscribe((error) => {
          // console.log(error);
          //this.loading = false;
        });
    }
  }
}
